/**
 * `<SettingsApiKeysRoute>` — `/w/:slug/settings/api-keys`
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 5.4`
 * + `functional-design/frontend-components.md § <ApiKeysSettings>`.
 *
 * Admin-only (gated by `useAbility().can("read", "ApiKey")` — non-admins
 * see the empty-permission alert; the API also enforces).
 *
 * Lists keys in a `<Table>` showing name, masked prefix, created date,
 * expiry, last-used, and a revoke action. "Generate key" opens
 * `<CreateApiKeyDialog>`; on success the raw token bubbles up via
 * `onCreated` and lands in `<NewKeyDisplayDialog>` exactly once — closing
 * that dialog drops the raw token from React state so it can't be
 * re-shown.
 */

import {
  authClient,
  useAbility,
  useWorkspace,
  type ApiKeyWithToken,
  type PublicApiKey,
} from "@cometchat-ai/auth/client";
import {
  Alert,
  AlertDescription,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  TableBody,
  TableHeader,
} from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { KeyRound, MoreHorizontal, Trash2 } from "../components/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { CreateApiKeyDialog } from "../components/create-api-key-dialog";
import { NewKeyDisplayDialog } from "../components/new-key-display-dialog";
import { RevokeApiKeyDialog } from "../components/revoke-api-key-dialog";

function formatDate(iso: string | null, fallback: string): string {
  if (!iso) return fallback;
  return new Date(iso).toLocaleDateString();
}

export function SettingsApiKeysRoute({
  createOpen: controlledCreateOpen,
  onCreateOpenChange,
  showToolbarButton = true,
}: {
  /** Controlled "create key" dialog state — when provided the parent owns the
   *  trigger (settings-auth renders it up in the tab row). Falls back to local
   *  state for standalone use. */
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
  /** Hide the in-component toolbar button when the parent renders its own. */
  showToolbarButton?: boolean;
} = {}) {
  const { t } = useTranslation("apikeys");
  const { workspace } = useWorkspace();
  const ability = useAbility();
  const canRead = ability.can("read", "ApiKey");
  const canCreate = ability.can("create", "ApiKey");
  const canRevoke = ability.can("revoke", "ApiKey");

  const [internalCreateOpen, setInternalCreateOpen] = useState(false);
  const createOpen = controlledCreateOpen ?? internalCreateOpen;
  const setCreateOpen = (open: boolean) => {
    if (controlledCreateOpen === undefined) setInternalCreateOpen(open);
    onCreateOpenChange?.(open);
  };
  const [createdKey, setCreatedKey] = useState<ApiKeyWithToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PublicApiKey | null>(null);

  const keysQuery = useQuery({
    queryKey: ["api-keys", workspace.slug],
    queryFn: () => authClient.listApiKeys(workspace.slug),
    enabled: canRead,
  });

  if (!canRead) {
    return (
      <div className="space-y-4" data-testid="settings-api-keys">
        <Alert data-testid="settings-api-keys-no-permission">
          <AlertDescription>{t("noPermission")}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const keys = keysQuery.data ?? [];

  return (
    <div data-testid="settings-api-keys">
      {showToolbarButton && canCreate ? (
        <div className="flex items-center justify-end gap-2 px-[18px] py-[18px]">
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
            data-testid="settings-api-keys-create"
          >
            <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
            {t("create")}
          </Button>
        </div>
      ) : null}

      {keysQuery.isPending ? (
        <div className="space-y-2.5 p-6" data-testid="api-keys-skeleton">
          <Skeleton className="h-9 w-full rounded-lg" />
          <Skeleton className="h-9 w-full rounded-lg" />
          <Skeleton className="h-9 w-2/3 rounded-lg" />
        </div>
      ) : keysQuery.error ? (
        <div className="px-6 py-12">
          <Alert variant="destructive">
            <AlertDescription>{keysQuery.error.message}</AlertDescription>
          </Alert>
        </div>
      ) : keys.length === 0 ? (
        <div
          className="mx-6 my-12 flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-12 text-center"
          data-testid="api-keys-empty"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <KeyRound className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">{t("empty.title")}</p>
            <p className="mx-auto max-w-sm text-[13px] text-muted-foreground">
              {t("empty.description")}
            </p>
          </div>
          {canCreate ? (
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="h-8 gap-1.5 rounded-md text-[13px]"
              data-testid="settings-api-keys-empty-create"
            >
              <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
              {t("empty.cta")}
            </Button>
          ) : null}
        </div>
      ) : (
        <DataGrid>
          <TableHeader>
            <DataGridHeaderRow>
              <DataGridHead className="w-[240px] pl-6 pr-3">{t("table.name")}</DataGridHead>
              <DataGridHead className="w-[180px]">{t("table.prefix")}</DataGridHead>
              <DataGridHead className="w-[130px]">{t("table.created")}</DataGridHead>
              <DataGridHead className="w-[130px]">{t("table.expires")}</DataGridHead>
              <DataGridHead className="w-[130px]" divider={canRevoke}>
                {t("table.lastUsed")}
              </DataGridHead>
              {canRevoke ? (
                <DataGridHead className="w-[120px] pr-6 text-right" divider={false}>
                  {t("table.actions")}
                </DataGridHead>
              ) : null}
            </DataGridHeaderRow>
          </TableHeader>
          <TableBody>
            {keys.map((apiKey) => (
              <DataGridRow
                key={apiKey.id}
                className="last:border-b-0"
                data-testid={`api-key-row-${apiKey.id}`}
              >
                <DataGridCell className="pl-6 pr-3">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-foreground">{apiKey.name}</span>
                    {apiKey.revokedAt ? (
                      <span
                        className="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs"
                        style={{
                          color: "var(--color-danger)",
                          background: "color-mix(in oklch, var(--color-danger) 12%, transparent)",
                        }}
                      >
                        {t("table.revoked")}
                      </span>
                    ) : null}
                  </div>
                </DataGridCell>
                <DataGridCell>
                  <code className="rounded-[5px] bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] px-1.5 py-0.5 font-mono text-xs text-foreground">
                    {apiKey.prefix}…•••
                  </code>
                </DataGridCell>
                <DataGridCell className="whitespace-nowrap">
                  {formatDate(apiKey.createdAt, "—")}
                </DataGridCell>
                <DataGridCell className="whitespace-nowrap">
                  {formatDate(apiKey.expiresAt, t("table.neverExpires"))}
                </DataGridCell>
                <DataGridCell className="whitespace-nowrap" divider={canRevoke}>
                  {formatDate(apiKey.lastUsedAt, t("table.neverUsed"))}
                </DataGridCell>
                {canRevoke ? (
                  <DataGridCell className="pr-6 text-right" divider={false}>
                    {apiKey.revokedAt ? null : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label={t("table.actions")}
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
                            data-testid={`api-key-actions-${apiKey.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem
                            className="gap-2 text-[13px] text-destructive focus:!bg-[color:color-mix(in_oklch,var(--color-danger)_12%,transparent)] focus:!text-destructive"
                            onClick={() => setRevokeTarget(apiKey)}
                            data-testid={`api-key-revoke-${apiKey.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            {t("revokeDialog.confirmYes")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </DataGridCell>
                ) : null}
              </DataGridRow>
            ))}
          </TableBody>
        </DataGrid>
      )}

      <CreateApiKeyDialog
        workspaceSlug={workspace.slug}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(key) => {
          setCreateOpen(false);
          setCreatedKey(key);
        }}
      />
      <NewKeyDisplayDialog keyRecord={createdKey} onClose={() => setCreatedKey(null)} />
      {revokeTarget ? (
        <RevokeApiKeyDialog
          workspaceSlug={workspace.slug}
          apiKeyId={revokeTarget.id}
          apiKeyName={revokeTarget.name}
          open={revokeTarget !== null}
          onOpenChange={(open) => {
            if (!open) setRevokeTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}
